// Global Instructions Rule Applied!

import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

// A campaign's per-account attachments (capital call notices, stage 5):
// files that belong to ONE account each, so the message to that account
// carries them and no other message does. The campaign's own attachments
// (email_campaigns__attachments) go to every recipient.
//
// GET /email/campaigns/:id/account-files → view__email_campaign_account_files.
//
// .strict() at every level: a key the model does not name is an error, not
// something to drop (CLAUDE.md).

/** One file of one account. file_url is the Storage path. */
export const EmailCampaignAccountFileSchema = z
  .object({
    file_urls_email_campaign_accounts__id: z.string(),
    file_urls_email_campaign_accounts__file_name: z.string(),
    file_urls_email_campaign_accounts__file_url: z.string(),
    file_urls_email_campaign_accounts__file_size_bytes: z.number().nullable(),
    // the file_urls_* table and row the file was copied from
    file_urls_email_campaign_accounts__source_table: z.string().nullable(),
    file_urls_email_campaign_accounts__source_row_id: z.string().nullable()
  })
  .strict()

export type EmailCampaignAccountFile = z.infer<typeof EmailCampaignAccountFileSchema>

/** One account that is staged or holds a file. */
export const EmailCampaignAccountFilesRowSchema = z
  .object({
    accounts__id: z.string(),
    accounts__name: z.string(),
    // false for an account that holds a file and has no live recipient
    prog__is_staged: z.boolean(),
    prog__file_count: z.number(),
    prog__total_bytes: z.number(),
    files: z.array(EmailCampaignAccountFileSchema)
  })
  .strict()

export type EmailCampaignAccountFilesRow = z.infer<typeof EmailCampaignAccountFilesRowSchema>

const AccountRefSchema = z
  .object({
    accounts__id: z.string(),
    accounts__name: z.string()
  })
  .strict()

const UnstagedAccountSchema = z
  .object({
    accounts__id: z.string(),
    accounts__name: z.string(),
    prog__file_count: z.number()
  })
  .strict()

/** What the queue gate checks (email_campaign__account_files_check). */
export const EmailCampaignAccountFilesSummarySchema = z
  .object({
    // the campaign was created to send per-account files
    required: z.boolean(),
    has_files: z.boolean(),
    staged_accounts: z.number(),
    accounts_with_files: z.number(),
    null_account_recipients: z.number(),
    // staged accounts with no file (only when required or has_files)
    missing: z.array(AccountRefSchema),
    // accounts with a file and no live recipient
    unstaged: z.array(UnstagedAccountSchema)
  })
  .strict()

export type EmailCampaignAccountFilesSummary = z.infer<typeof EmailCampaignAccountFilesSummarySchema>

export const EmailCampaignAccountFilesSchema = z
  .object({
    data: z.array(EmailCampaignAccountFilesRowSchema),
    prog__summary: EmailCampaignAccountFilesSummarySchema
  })
  .strict()

export type EmailCampaignAccountFiles = z.infer<typeof EmailCampaignAccountFilesSchema>

export function validateEmailCampaignAccountFiles(data: unknown): EmailCampaignAccountFiles {
  return validateWithZodSchema(EmailCampaignAccountFilesSchema, data, 'email campaign account files')
}

/** True when the campaign carries per-account attachments: the queue then
 *  needs a file for every staged account. */
export const carriesAccountFiles = (summary: EmailCampaignAccountFilesSummary | null | undefined): boolean =>
  !!summary && (summary.required || summary.has_files)
